import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Typography,
  Spin,
  Alert,
  Row,
  Col,
  Empty,
  Input,
  Select,
  Space,
  Button,
  Tooltip
} from 'antd';
import {
  ExperimentOutlined,
  FileSearchOutlined,
  ScanOutlined,
  HeartOutlined,
  SearchOutlined,
  FilterOutlined,
  CalendarOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { usePatientPortal } from '../context/PatientPortalContext';
import PatientPortalLabResults from './PatientPortalLabResults';
import PatientPortalImagingResults from './PatientPortalImagingResults';
import PatientPortalDiagnosticsProcedures from './PatientPortalDiagnosticsProcedures';
import './PatientPortalDiagnosticsPage.css';
import MomentDatePicker from './MomentDatePicker';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = MomentDatePicker;
const { Option } = Select;

const PatientPortalDiagnosticsPage = () => {
  const { patient, getPortalAxios } = usePatientPortal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('labs');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Data states
  const [diagnosticsData, setDiagnosticsData] = useState({
    labResults: [],
    imagingResults: [],
    procedures: [],
    providers: [],
    categories: []
  });

  // Fetch diagnostics data
  useEffect(() => {
    const fetchDiagnosticsData = async () => {
      if (!patient?._id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const portalAxios = getPortalAxios();



        // Fetch all diagnostic data concurrently
        const [labRes, imagingRes, proceduresRes] = await Promise.all([
          portalAxios.get('/portal/lab-results'),
          portalAxios.get('/portal/imaging-results'),
          portalAxios.get('/portal/diagnostic-procedures')
        ]);



        // Extract unique providers and categories for filtering
        const allResults = [
          ...(labRes.data.data?.results || []),
          ...(imagingRes.data.data?.results || []),
          ...(proceduresRes.data.data?.results || [])
        ];

        const providers = [...new Set(allResults.map(r => r.orderingProvider?.name).filter(Boolean))];
        const categories = [...new Set(allResults.map(r => r.category).filter(Boolean))];

        setDiagnosticsData({
          labResults: labRes.data.data?.results || [],
          imagingResults: imagingRes.data.data?.results || [],
          procedures: proceduresRes.data.data?.results || [],
          providers,
          categories
        });
      } catch (err) {
        console.error('Error fetching diagnostics data:', err);
        setError(`Unable to load diagnostic results: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnosticsData();
  }, [patient?._id, getPortalAxios]);

  // Filter functions
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleProviderChange = (value) => {
    setSelectedProvider(value);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateRange(null);
    setSelectedProvider(null);
    setSelectedCategory(null);
  };

  // Get filtered data based on current filters
  const getFilteredData = (data) => {
    return data.filter(item => {
      // Search term filter
      if (searchTerm && !item.testName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !item.studyDescription?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !item.procedureName?.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Date range filter
      if (dateRange && dateRange.length === 2) {
        const itemDate = new Date(item.resultDate || item.studyDate || item.procedureDate);
        if (itemDate < dateRange[0] || itemDate > dateRange[1]) {
          return false;
        }
      }
      
      // Provider filter
      if (selectedProvider && item.orderingProvider?.name !== selectedProvider) {
        return false;
      }
      
      // Category filter
      if (selectedCategory && item.category !== selectedCategory) {
        return false;
      }
      
      return true;
    });
  };

  const renderFilters = () => (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Search tests, studies..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <RangePicker
            placeholder={['Start Date', 'End Date']}
            value={dateRange}
            onChange={handleDateRangeChange}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Provider"
            value={selectedProvider}
            onChange={handleProviderChange}
            style={{ width: '100%' }}
            allowClear
          >
            {diagnosticsData.providers.map(provider => (
              <Option key={provider} value={provider}>{provider}</Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Category"
            value={selectedCategory}
            onChange={handleCategoryChange}
            style={{ width: '100%' }}
            allowClear
          >
            {diagnosticsData.categories.map(category => (
              <Option key={category} value={category}>{category}</Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} md={4}>
          <Space>
            <Button 
              icon={<FilterOutlined />} 
              onClick={clearFilters}
              disabled={!searchTerm && !dateRange && !selectedProvider && !selectedCategory}
            >
              Clear
            </Button>
            <Tooltip title="Export Results">
              <Button icon={<DownloadOutlined />} />
            </Tooltip>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  const renderOverview = () => {
    const totalLabs = diagnosticsData.labResults.length;
    const totalImaging = diagnosticsData.imagingResults.length;
    const totalProcedures = diagnosticsData.procedures.length;
    const totalResults = totalLabs + totalImaging + totalProcedures;

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small" className="diagnostic-overview-card">
            <div className="diagnostic-stat">
              <ExperimentOutlined className="diagnostic-icon lab-icon" />
              <div>
                <div className="diagnostic-number">{totalLabs}</div>
                <div className="diagnostic-label">Lab Results</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" className="diagnostic-overview-card">
            <div className="diagnostic-stat">
              <ScanOutlined className="diagnostic-icon imaging-icon" />
              <div>
                <div className="diagnostic-number">{totalImaging}</div>
                <div className="diagnostic-label">Imaging Studies</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" className="diagnostic-overview-card">
            <div className="diagnostic-stat">
              <HeartOutlined className="diagnostic-icon procedure-icon" />
              <div>
                <div className="diagnostic-number">{totalProcedures}</div>
                <div className="diagnostic-label">Procedures</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" className="diagnostic-overview-card">
            <div className="diagnostic-stat">
              <FileSearchOutlined className="diagnostic-icon total-icon" />
              <div>
                <div className="diagnostic-number">{totalResults}</div>
                <div className="diagnostic-label">Total Results</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" tip="Loading your diagnostic results..." />
      </div>
    );
  }

  return (
    <div className="patient-portal-diagnostics" style={{ padding: '24px', background: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
          <ExperimentOutlined style={{ marginRight: 8 }} />
          Diagnostic Results
        </Title>
        <Paragraph style={{ margin: '8px 0 0 0', color: '#666' }}>
          View your laboratory results, imaging studies, and diagnostic procedures
        </Paragraph>
      </div>

      {error && (
        <Alert
          message="Error Loading Data"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Overview Cards */}
      {renderOverview()}

      {/* Filters */}
      {renderFilters()}

      {/* Main Content Tabs */}
      <Card>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          size="large"
          tabBarStyle={{ marginBottom: 24 }}
        >
          <TabPane 
            tab={
              <span>
                <ExperimentOutlined />
                Lab Results ({diagnosticsData.labResults.length})
              </span>
            } 
            key="labs"
          >
            <PatientPortalLabResults 
              data={getFilteredData(diagnosticsData.labResults)}
              loading={loading}
            />
          </TabPane>
          
          <TabPane
            tab={
              <span>
                <ScanOutlined />
                Imaging Studies ({diagnosticsData.imagingResults.length})
              </span>
            }
            key="imaging"
          >
            <PatientPortalImagingResults 
              data={getFilteredData(diagnosticsData.imagingResults)}
              loading={loading}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <HeartOutlined />
                Procedures ({diagnosticsData.procedures.length})
              </span>
            } 
            key="procedures"
          >
            <PatientPortalDiagnosticsProcedures 
              data={getFilteredData(diagnosticsData.procedures)}
              loading={loading}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default PatientPortalDiagnosticsPage;
